import type * as React from 'react';
import {
  createRootRoute, createRoute, createRouter, Outlet, useRouterState,
} from '@tanstack/react-router';
import { AppShell } from '../components/layout/AppShell.js';
import { LivePage } from '../features/live/LivePage.js';
import { LiveQueuesPage } from '../features/live/LiveQueuesPage.js';
import { WallboardPage } from '../features/live/WallboardPage.js';
import { DashboardPage } from '../features/dashboard/DashboardPage.js';
import { AccountsPage } from '../features/accounts/AccountsPage.js';
import { AccountDetailPage } from '../features/accounts/AccountDetailPage.js';
import { PlatformUsersPage } from '../features/accounts/PlatformUsersPage.js';
import { AccountSecurityPage } from '../features/account/AccountSecurityPage.js';
import { ExtensionTypesPage } from '../features/products/ExtensionTypesPage.js';
import { CallPlansPage } from '../features/products/CallPlansPage.js';
import { CarriersPage } from '../features/carriers/CarriersPage.js';
import { NumberInventoryPage } from '../features/carriers/NumberInventoryPage.js';
import { HandsetCatalogPage } from '../features/provisioning/HandsetCatalogPage.js';
import { TemplatesPage } from '../features/provisioning/TemplatesPage.js';
import { ActivityPage } from '../features/accounts/ActivityPage.js';
import { JobsPage } from '../features/accounts/JobsPage.js';
import { RecordingStoragePage } from '../features/accounts/RecordingStoragePage.js';
import { ProviderKeysPage } from '../features/accounts/ProviderKeysPage.js';
import { NodesPage } from '../features/accounts/NodesPage.js';
import { SipSecurityPage } from '../features/accounts/SipSecurityPage.js';
import { SettingsPage } from '../features/accounts/SettingsPage.js';
import { BillingEntityPage } from '../features/billing/BillingEntityPage.js';
import { InvoicesPage } from '../features/billing/InvoicesPage.js';
import { BillingRunsPage } from '../features/billing/BillingRunsPage.js';
import { ResellersPage } from '../features/accounts/ResellersPage.js';
import { ExtensionsPage } from '../features/extensions/ExtensionsPage.js';
import { CdrsPage } from '../features/cdrs/CdrsPage.js';
import { BillingPage, RateSheetsPage } from '../features/billing/BillingPage.js';
import { PlaceholderPage } from '../features/misc/PlaceholderPage.js';
import { ManageAccountPage } from '../features/manage/ManageAccountPage.js';
import { AgentWorkspace } from '../features/agent/AgentWorkspace.js';
import { AttendancePage } from '../features/agent/AttendancePage.js';
import { FraudPage } from '../features/fraud/FraudPage.js';

/**
 * The root renders the console shell for every route EXCEPT /manage/*.
 *
 * Account configuration is a full-window working session on one customer, so it
 * gets its own chrome and its own browser tab. Wrapping it in the console's
 * global navigation would invite wandering out of a half-finished change.
 */
const rootRoute = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  // The workspace sits in the console's frame (owner, 3 October 2026: "where did the side panel
  // go???"): an agent moves between it, their calls and their extension all day.
  if (path.startsWith('/manage/')) return <Outlet />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const route = (path: string, component: () => React.JSX.Element) =>
  createRoute({ getParentRoute: () => rootRoute, path, component });

/** The platform's own phone templates: the same screen as an account's, with no account. */
const PlatformTemplatesPage = () => <TemplatesPage tenantId={null} />;

/**
 * Route tree mirrors the navigation, and per-account detail lives under
 * /accounts/$accountId rather than as top-level screens.
 *
 * That is what makes a support link unambiguous: the URL names the customer, so
 * pasting one cannot land a colleague on a different account than the one being
 * discussed.
 */
const routeTree = rootRoute.addChildren([
  route('/', DashboardPage),

  /**
   * Realtime.
   *
   * /live is kept because links to it are already in circulation — a support
   * conversation that pasted one should not dead-end. It renders the same page
   * rather than redirecting, so the URL a colleague opens still works.
   */
  route('/realtime/extensions', LivePage),
  route('/realtime/queues', LiveQueuesPage),
  route('/realtime/wallboard', WallboardPage),
  route('/realtime/wallboard/$', WallboardPage),
  route('/live', LivePage),
  route('/activity', ActivityPage),

  // Accounts, and everything about one account behind its id.
  route('/accounts', AccountsPage),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/accounts/$accountId',
    component: AccountDetailPage,
  }),
  // Below an account is a path, never a query string: /accounts/meridian/extensions/1001.
  // The page reads it with useSubroute (lib/subroute.tsx).
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/accounts/$accountId/$',
    component: AccountDetailPage,
  }),
  // Full account configuration — opened in its own browser tab from the console.
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/manage/$tenantId',
    component: ManageAccountPage,
  }),
  // /manage/meridian/extensions/1001/phone: the section, then whatever it shows.
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/manage/$tenantId/$',
    component: ManageAccountPage,
  }),

  // The agent workspace: the browser phone and the calls around it (docs/36 SS8).
  route('/agent', AgentWorkspace),
  // What the workspace's shifts add up to. Inside the console's chrome, because it is read
  // between other things rather than worked in.
  route('/realtime/attendance', AttendancePage),

  // Toll fraud (docs/39): holds and alerts across whatever this person can see.
  route('/fraud', FraudPage),
  route('/resellers', ResellersPage),
  route('/users', PlatformUsersPage),
  // The signed-in person's own security: two-step verification.
  route('/account/security', AccountSecurityPage),

  // Products
  route('/products/extension-types', ExtensionTypesPage),
  route('/products/call-plans', CallPlansPage),
  route('/products/rate-sheets', RateSheetsPage),
  // One sheet, by its name: /products/rate-sheets/switch-telecom-retail.
  route('/products/rate-sheets/$', RateSheetsPage),

  // Inventory
  route('/inventory/carriers', CarriersPage),
  route('/inventory/numbers', NumberInventoryPage),
  route('/inventory/handsets', HandsetCatalogPage),
  // Phone templates every account can use, the open one by its slug: /inventory/templates/reception.
  route('/inventory/templates', PlatformTemplatesPage),
  route('/inventory/templates/$', PlatformTemplatesPage),

  // Billing
  route('/billing/runs', BillingRunsPage),
  route('/billing/invoices', InvoicesPage),
  // An issued invoice by its number: /billing/invoices/INV00012.
  route('/billing/invoices/$', InvoicesPage),
  route('/billing/setup', BillingEntityPage),
  // Its first name, kept so an old link still lands.
  route('/billing/entity', BillingEntityPage),

  // Platform
  route('/platform/nodes', NodesPage),
  route('/platform/jobs', JobsPage),
  route('/platform/provider-keys', ProviderKeysPage),
  route('/platform/recording-storage', RecordingStoragePage),
  route('/platform/sip-security', SipSecurityPage),
  route('/platform/settings', SettingsPage),

  // Tenant-scoped users: their own PBX, no account list to drill through.
  route('/my/extensions', ExtensionsPage),
  route('/my/extensions/$', ExtensionsPage),
  route('/my/calls', CdrsPage),
  route('/my/billing', BillingPage),
  // The tab below it: /my/billing/payments is where PayFast sends a payer back to.
  route('/my/billing/$', BillingPage),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}
